/**
 * tbb-auction-gapfill.js - fills the <auction-info> cards Auction Plus leaves empty.
 *
 * HISTORY
 * This is the surviving half of tbb-boost-auction.js (session 28). That file
 * did two jobs: (1) draw an auction preview inside Boost Search & Discovery
 * product cards, and (2) fill native <auction-info> elements that Auction
 * Plus never hydrates. Boost was uninstalled 2026-09-22, so job (1) is gone
 * with it. Job (2) is load-bearing and lives here, renamed, with the same
 * markup, the same data path and the same timings. Split 2026-09-24.
 *
 * WHY THIS EXISTS
 * Auction Plus only hydrates <auction-info> for products it printed into the
 * page HTML at SERVER-RENDER time. Our ai-carousel is engine-personalised: it
 * swaps [data-ai-content] AFTER that render, so the engine can surface an
 * auction AP never announced. That card's <auction-info> then renders as an
 * empty `<!---->` - no price, no bids, no error. Diagnosed 2026-07-14 on the
 * Happy Gilmore PDP: 9/10 cards hydrated, the Game Boy Advance did not, purely
 * because it was absent from AP's 12-handle server payload while the engine
 * put it in the row.
 *
 * Merging into window.auctionPlusSetup.auctions does NOT help - verified live:
 * the registry already contained the missing auction (15 entries) and the card
 * still rendered empty, because AP does not consult the registry on hydrate.
 * So we render the markup ourselves.
 *
 * We only ever touch elements AP left EMPTY, and we render AP's exact inner
 * markup so tbb-reskin.css styles it identically (verified: same pink
 * rgb(254,138,237), same 25px, same label/bid-count colours as a real card).
 * If AP ever hydrates late, Vue overwrites us - which is the desired outcome.
 *
 * Same root cause as the parked "auction-info blank in browsing history" bug.
 *
 * DATA SOURCE
 * `window.auctionPlusSetup.auctions` - populated by Auction Plus on collection
 * and product pages with the active-auction registry (handle, start_price,
 * high_bid, starts_at, ends_at, status). On pages where the registry is not
 * populated (homepage, search, vendor pages) we fetch the auction collection
 * page once in the background and lift the auctions out of its server-rendered
 * `auctionApp.updateAuctions([...])` calls. No API key needed.
 *
 * RESILIENCE
 * Idempotent (a filled element is marked and never touched again), one shared
 * one-second ticker keeps every countdown current, and if auction data cannot
 * be resolved it gives up silently. It never blocks the page.
 */
(function () {
  'use strict';

  // Class hook the ticker uses to find the phrase it updates. Purely a JS
  // handle: no CSS targets it (the native auction-info rules in tbb-reskin.css
  // style the markup, exactly as they style AP's own).
  var PHRASE_CLASS = 'tbb-auction-gapfill__phrase';

  // Collection handle(s) that contain auction products. Fetched as the fallback
  // registry when auctionPlusSetup.auctions is missing on the page.
  var AUCTION_COLLECTION_HANDLES = [
    'nostalgia-auction'
  ];

  var auctionsByHandle = null; // built once when the registry is available
  var liveSlots = [];          // {el, auction, targetTime, isStarted, ...}
  var fetchAttempted = false;  // only try the fallback once

  /* ── Index builder ──────────────────────────────────────────── */

  function buildIndex() {
    if (auctionsByHandle) return auctionsByHandle;
    var setup = window.auctionPlusSetup;
    if (!setup || !Array.isArray(setup.auctions)) return null;
    var idx = Object.create(null);
    for (var i = 0; i < setup.auctions.length; i++) {
      var a = setup.auctions[i];
      if (a && a.partner_product_handle && !a.canceled_at) {
        // Skip ended auctions - let the normal price render.
        if (a.status === 'ended' || a.status === 'closed') continue;
        idx[a.partner_product_handle] = a;
      }
    }
    auctionsByHandle = idx;
    return idx;
  }

  /* ── Fallback: lift auction data out of the collection page HTML ── */

  /**
   * Extracts auction objects from a collection page's HTML. Auction Plus
   * renders multiple `auctionApp.updateAuctions([...])` calls, one per auction
   * item, each holding a single-element array. We collect them all.
   */
  function extractAuctionsFromHTML(html) {
    var marker = 'updateAuctions(';
    var results = [];
    var searchFrom = 0;

    while (true) {
      var idx = html.indexOf(marker, searchFrom);
      if (idx === -1) break;

      var arrayStart = idx + marker.length; // should point at [
      if (html.charAt(arrayStart) !== '[') {
        searchFrom = arrayStart;
        continue;
      }

      // Bracket-match to find the closing ]
      var depth = 0;
      var inStr = false;
      var esc = false;
      var found = false;
      for (var i = arrayStart; i < html.length && i < arrayStart + 20000; i++) {
        var ch = html.charAt(i);
        if (esc) { esc = false; continue; }
        if (ch === '\\' && inStr) { esc = true; continue; }
        if (ch === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (ch === '[') depth++;
        else if (ch === ']') {
          depth--;
          if (depth === 0) {
            try {
              var arr = JSON.parse(html.substring(arrayStart, i + 1));
              results = results.concat(arr);
            } catch (e) { /* skip malformed entry */ }
            found = true;
            searchFrom = i + 1;
            break;
          }
        }
      }
      if (!found) break; // malformed - stop searching
    }

    return results.length ? results : null;
  }

  /**
   * Fetches the auction collection page(s), merges every auction into
   * auctionPlusSetup.auctions, then calls back so the sweep can run.
   */
  function fetchAuctionRegistry(callback) {
    if (fetchAttempted) return;
    fetchAttempted = true;

    var allAuctions = [];
    var pending = AUCTION_COLLECTION_HANDLES.length;

    AUCTION_COLLECTION_HANDLES.forEach(function (handle) {
      fetch('/collections/' + handle, {
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' }
      })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var auctions = extractAuctionsFromHTML(html);
          if (auctions && auctions.length) {
            allAuctions = allAuctions.concat(auctions);
          }
        })
        .catch(function () { /* silent - never break the page */ })
        .finally(function () {
          pending--;
          if (pending <= 0 && allAuctions.length) {
            // Inject into the global setup so buildIndex() works.
            if (!window.auctionPlusSetup) window.auctionPlusSetup = {};
            window.auctionPlusSetup.auctions = allAuctions;
            auctionsByHandle = null; // reset so buildIndex re-runs
            callback();
          }
        });
    });
  }

  /* ── Formatting (matches the native widget's voice) ───────────── */

  // Native phrasing: "23 hours left", "Starts in 5 minutes". Singular/plural;
  // rounds DOWN to the largest unit so it matches the native widget's text.
  function fmtRelative(ms) {
    if (ms <= 0) return 'now';
    var s = Math.floor(ms / 1000);
    var m = Math.floor(s / 60);
    var h = Math.floor(m / 60);
    var d = Math.floor(h / 24);
    if (d >= 1) return d + (d === 1 ? ' day' : ' days');
    if (h >= 1) return h + (h === 1 ? ' hour' : ' hours');
    if (m >= 1) return m + (m === 1 ? ' minute' : ' minutes');
    return s + (s === 1 ? ' second' : ' seconds');
  }

  /**
   * "16 bids" / "1 bid" - matches the native widget's bid-count span exactly
   * (same classes, so tbb-reskin.css styles it identically). Empty when zero.
   */
  function bidCountHtml(auction) {
    var n = auction.total_bids;
    if (!n || n < 1) return '';
    return ' <span class="px-1 text-right text-sm font-medium text-gray-500">' +
      n + (n === 1 ? ' bid' : ' bids') + '</span>';
  }

  /* ── Slot builder (mirrors the native <auction-info> inner HTML) ──
     Always the native voice ("1 day left" + green status dot while running),
     because this slot sits beside genuine AP-hydrated cards in the same row and
     "Ends in 1 day" next to "1 day left" reads as a bug. The old Boost-card
     voice went with Boost. */

  function buildSlot(auction) {
    var now = Date.now();
    var startsAt = auction.starts_at ? new Date(auction.starts_at).getTime() : 0;
    var endsAt = auction.ends_at ? new Date(auction.ends_at).getTime() : 0;
    var isStarted = startsAt && startsAt <= now;
    // AP's shape is high_bid.amount.display, NOT high_bid.display. Reading
    // `.display` off high_bid always returned undefined, so hasBid was ALWAYS
    // false and every card silently showed "Starting Bid" + the opening price
    // instead of the live bid. Fixed 2026-07-14.
    var bidAmount = auction.high_bid && auction.high_bid.amount;
    var hasBid = !!(bidAmount && bidAmount.display);
    var priceObj = hasBid ? bidAmount : auction.start_price;
    if (!priceObj || !priceObj.display) return null;

    // `suffix` carries the native voice (" left") for a running auction;
    // `timeLabel` ("Starts in ") for a scheduled one. Exactly one is non-empty.
    var timeLabel, suffix, targetTime, targetIso;
    if (isStarted && endsAt) {
      timeLabel = '';
      suffix = ' left';
      targetTime = endsAt;
      targetIso = auction.ends_at;
    } else if (startsAt) {
      timeLabel = 'Starts in ';
      suffix = '';
      targetTime = startsAt;
      targetIso = auction.starts_at;
    } else {
      timeLabel = '';
      suffix = '';
      targetTime = 0;
      targetIso = '';
    }
    var hasTime = !!targetTime;
    var remaining = targetTime - now;
    // An ended auction shows 'Ended', never "now left".
    var ended = isStarted && endsAt && remaining <= 0;
    var label = hasBid ? (ended ? 'Winning Bid' : 'Current Bid') : 'Starting Bid';
    var phrase = ended ? 'Ended' : (timeLabel + fmtRelative(remaining) + suffix);
    // Native marks a RUNNING auction with a green dot; scheduled/ended grey.
    var dotClass = (isStarted && !ended) ? 'bg-green-700' : 'bg-gray-300';

    // NOTE: never wrap this in a literal <auction-info>: it is a Vue custom
    // element that wipes its slot the moment it connects. We render the same
    // INNER markup the native widget produces and the native CSS styles it.
    var html =
      '<div class="auction auction-container">' +
        '<div class="my-2 flex justify-between">' +
          '<div class="flex flex-col text-sm gap-1">' +
            '<span class="text-sm uppercase text-gray-500">' + label + '</span>' +
            '<strong>' +
              '<span class="text-lg">' + priceObj.display + '</span>' +
              bidCountHtml(auction) +
            '</strong>' +
          '</div>' +
        '</div>' +
        (hasTime
          ? '<div class="flex text-sm font-bold my-2">' +
              '<time datetime="' + targetIso + '">' +
                '<span><span class="mr-1 inline-block h-2 w-2 rounded-full ' + dotClass + '"></span></span>' +
                '<span class="' + PHRASE_CLASS + '">' + phrase + '</span>' +
              '</time>' +
            '</div>'
          : '') +
      '</div>';

    return {
      html: html,
      auction: auction,
      targetTime: targetTime,
      timeLabel: timeLabel,
      suffix: suffix,
      isStarted: isStarted
    };
  }

  /* ── The gap-filler ──────────────────────────────────────────── */

  function fillEmptyNativeAuctionInfo(root) {
    var idx = buildIndex();
    if (!idx) return;
    var nodes = (root || document).querySelectorAll('auction-info[product-handle]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      // Never fight AP for an element it already rendered.
      if ((el.textContent || '').trim().length) continue;
      if (el.getAttribute('data-tbb-filled') === '1') continue;

      var auction = idx[el.getAttribute('product-handle')];
      if (!auction) continue;

      var slot = buildSlot(auction);
      if (!slot) continue;

      el.setAttribute('data-tbb-filled', '1');
      el.innerHTML = slot.html;
      slot.el = el; // the ticker updates the element we actually filled
      liveSlots.push(slot);
    }
  }

  /* ── Countdown ticker ───────────────────────────────────────── */

  function tick() {
    var now = Date.now();
    var keep = [];
    for (var i = 0; i < liveSlots.length; i++) {
      var s = liveSlots[i];
      if (!s.el.isConnected) continue; // dropped from the DOM by a re-render
      var phraseEl = s.el.querySelector('.' + PHRASE_CLASS);
      if (!phraseEl) { keep.push(s); continue; }

      // Auction transitioned from scheduled to active mid-session.
      if (!s.isStarted) {
        var startsAt = s.auction.starts_at ? new Date(s.auction.starts_at).getTime() : 0;
        if (startsAt && startsAt <= now) {
          s.isStarted = true;
          s.timeLabel = '';
          s.suffix = ' left';
          s.targetTime = s.auction.ends_at ? new Date(s.auction.ends_at).getTime() : 0;
          var dot = s.el.querySelector('.rounded-full');
          if (dot) dot.classList.replace('bg-gray-300', 'bg-green-700');
        }
      }
      var remaining = s.targetTime - now;
      if (s.isStarted && remaining <= 0) {
        phraseEl.textContent = 'Ended';
        var endDot = s.el.querySelector('.rounded-full');
        if (endDot) endDot.classList.replace('bg-green-700', 'bg-gray-300');
        var labelEl = s.el.querySelector('.text-sm.uppercase');
        if (labelEl && labelEl.textContent === 'Current Bid') {
          labelEl.textContent = 'Winning Bid';
        }
      } else {
        phraseEl.textContent = s.timeLabel + fmtRelative(remaining) + (s.suffix || '');
      }
      keep.push(s);
    }
    liveSlots = keep;
  }
  setInterval(tick, 1000);

  /* ── Watch for carousel swaps that bring new <auction-info> ─────── */

  var mo = new MutationObserver(function (mutations) {
    var sawAuctionInfo = false;
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        // The ai-carousel swaps whole [data-ai-content] blocks in, which brings
        // native <auction-info> elements with it.
        if ((n.matches && n.matches('auction-info')) ||
            (n.querySelector && n.querySelector('auction-info'))) {
          sawAuctionInfo = true;
          break;
        }
      }
      if (sawAuctionInfo) break;
    }
    if (sawAuctionInfo) {
      // Let AP have first refusal on the freshly-added elements; we only fill
      // what it leaves empty. 1200ms clears its hydrate in practice.
      setTimeout(function () { fillEmptyNativeAuctionInfo(document); }, 1200);
    }
  });

  /* ── Bootstrap ──────────────────────────────────────────────── */

  function start() {
    var setup = window.auctionPlusSetup;
    var hasRegistry = setup && Array.isArray(setup.auctions) && setup.auctions.length > 0;

    if (hasRegistry) {
      // Collection / product page: the registry is already on the page.
      fillEmptyNativeAuctionInfo(document);
    } else {
      // Homepage, search, vendor page: fetch the registry, then sweep.
      fetchAuctionRegistry(function () { fillEmptyNativeAuctionInfo(document); });
    }

    // Observe for ai-carousel content swaps that bring native <auction-info>.
    mo.observe(document.body, { childList: true, subtree: true });

    // Deferred sweeps: on first paint AP has not hydrated yet, so an immediate
    // pass would fill cards AP is about to render itself. Give it room, then
    // fill only what is still empty. Two passes covers a slow AP response
    // without racing it.
    setTimeout(function () { fillEmptyNativeAuctionInfo(document); }, 2500);
    setTimeout(function () { fillEmptyNativeAuctionInfo(document); }, 6000);
  }

  // Wait for DOM + the auctionPlusSetup object (not necessarily .auctions).
  function whenReady(fn) {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (window.auctionPlusSetup) {
        clearInterval(iv);
        fn();
      } else if (tries > 40) {
        clearInterval(iv); // give up after ~20s
      }
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { whenReady(start); });
  } else {
    whenReady(start);
  }
})();
